import * as WebBrowser from 'expo-web-browser';
import { Platform } from 'react-native';
import { billingAPI } from '../api';
import { buildPublicWebUrl } from '../publicUrl';
import { bindCheckoutAttempt, type CheckoutIntent } from './checkoutIntent';

export type BillingProviderName = 'stripe' | 'manual' | 'wompi';
export type CheckoutSessionMode = 'redirect' | 'overlay' | 'embedded';
export type CheckoutAttemptStatus =
  | 'pending'
  | 'approved'
  | 'declined'
  | 'error'
  | 'voided'
  | 'expired';

export interface CheckoutSessionResponse {
  provider: BillingProviderName;
  mode: CheckoutSessionMode;
  url?: string;
  externalRef?: string;
  attemptId?: string;
  amountInCents?: number;
  currencyCode?: string;
  priceReference?: string;
  status?: CheckoutAttemptStatus;
  expiresAt?: string;
  plan?: CheckoutIntent['plan'];
  cycle?: CheckoutIntent['cycle'];
}

export type CheckoutOutcome =
  | { status: 'opened' | 'redirected' | 'dismissed' | 'completed'; attemptId: string }
  | {
      status: 'unavailable';
      reason:
        | 'checkout-unavailable'
        | 'no-url'
        | 'invalid-url'
        | 'invalid-session'
        | 'unsupported-provider';
    };

export interface CheckoutContext {
  tier: CheckoutIntent['plan'];
  cycle: CheckoutIntent['cycle'];
}

export interface CheckoutClient {
  startCheckout(
    session: CheckoutSessionResponse,
    context: CheckoutContext,
  ): Promise<CheckoutOutcome>;
}

function isTrustedWompiUrl(value: string | undefined): value is string {
  if (!value) return false;
  try {
    const url = new URL(value);
    return (
      url.protocol === 'https:' &&
      url.hostname === 'checkout.wompi.co' &&
      url.pathname === '/p/' &&
      !url.username &&
      !url.password
    );
  } catch {
    return false;
  }
}

function isTrustedStripeUrl(value: string | undefined): value is string {
  if (!value) return false;
  try {
    const url = new URL(value);
    return (
      url.protocol === 'https:' &&
      url.hostname === 'checkout.stripe.com' &&
      !url.username &&
      !url.password &&
      (url.pathname.startsWith('/c/pay/') || url.pathname.startsWith('/pay/'))
    );
  } catch {
    return false;
  }
}

export function buildCheckoutReturnUrl(path: string): string | undefined {
  return buildPublicWebUrl(path);
}

function createRedirectCheckoutClient(
  isTrustedUrl: (value: string | undefined) => value is string,
): CheckoutClient {
  return {
    async startCheckout(session) {
      if (!session.url) return { status: 'unavailable', reason: 'no-url' };
      if (!isTrustedUrl(session.url)) {
        return { status: 'unavailable', reason: 'invalid-url' };
      }
      if (!session.attemptId) return { status: 'unavailable', reason: 'invalid-session' };

      if (Platform.OS === 'web') {
        window.location.assign(session.url);
        return { status: 'redirected', attemptId: session.attemptId };
      }

      const result = await WebBrowser.openBrowserAsync(session.url);
      return {
        status: result.type === 'cancel' || result.type === 'dismiss' ? 'dismissed' : 'opened',
        attemptId: session.attemptId,
      };
    },
  };
}

const wompiCheckoutClient = createRedirectCheckoutClient(isTrustedWompiUrl);
const stripeCheckoutClient = createRedirectCheckoutClient(isTrustedStripeUrl);

const unsupportedCheckoutClient: CheckoutClient = {
  async startCheckout() {
    return { status: 'unavailable', reason: 'unsupported-provider' };
  },
};

export function getCheckoutClient(provider: BillingProviderName): CheckoutClient {
  if (provider === 'wompi') return wompiCheckoutClient;
  if (provider === 'stripe') return stripeCheckoutClient;
  return unsupportedCheckoutClient;
}

export interface StartCheckoutParams {
  organizationId: string;
  intent: CheckoutIntent;
  successUrl: string;
  cancelUrl: string;
  expectedRoute: {
    provider: 'wompi' | 'stripe';
    priceReference?: string;
  };
}

export async function startProviderCheckout(params: StartCheckoutParams): Promise<CheckoutOutcome> {
  const { data } = await billingAPI.createCheckoutSession(
    {
      organizationId: params.organizationId,
      plan: params.intent.plan,
      cycle: params.intent.cycle,
      successUrl: params.successUrl,
      cancelUrl: params.cancelUrl,
    },
    params.intent.idempotencyKey,
  );
  const session = data as CheckoutSessionResponse;

  if (
    !['wompi', 'stripe'].includes(session.provider) ||
    session.mode !== 'redirect' ||
    !session.attemptId ||
    !session.expiresAt ||
    session.plan !== params.intent.plan ||
    session.cycle !== params.intent.cycle ||
    !Number.isSafeInteger(session.amountInCents) ||
    (session.amountInCents ?? 0) <= 0 ||
    (session.provider === 'wompi' && session.currencyCode !== 'COP') ||
    (session.provider === 'stripe' && (session.currencyCode !== 'USD' || !session.priceReference))
  ) {
    return { status: 'unavailable', reason: 'invalid-session' };
  }
  if (
    session.provider !== params.expectedRoute.provider ||
    session.priceReference !== params.expectedRoute.priceReference
  ) {
    return { status: 'unavailable', reason: 'invalid-session' };
  }
  if (!session.url) return { status: 'unavailable', reason: 'no-url' };
  const hasTrustedUrl =
    session.provider === 'wompi' ? isTrustedWompiUrl(session.url) : isTrustedStripeUrl(session.url);
  if (!hasTrustedUrl) {
    return { status: 'unavailable', reason: 'invalid-url' };
  }

  await bindCheckoutAttempt(params.intent, {
    organizationId: params.organizationId,
    attemptId: session.attemptId,
    expiresAt: session.expiresAt,
  });

  if (session.status === 'approved') {
    return { status: 'completed', attemptId: session.attemptId };
  }
  if (session.status === 'expired' || session.status === 'voided') {
    return { status: 'unavailable', reason: 'checkout-unavailable' };
  }

  return getCheckoutClient(session.provider).startCheckout(session, {
    tier: params.intent.plan,
    cycle: params.intent.cycle,
  });
}
